import { useEffect, useMemo, useRef } from 'react';

import FormattingService from '@core/services/FormattingService';
import PrecisionService from '@core/services/PrecisionService';
import { isEmpty } from '@utils/validationUtils';

import {
  AmendmentDetailsResponse,
  AmendmentMoreDetailsResponse,
  AmendmentPolicyCoverDto,
  AmendmentPolicyCoverForm,
  AmendmentPolicyPerson,
  BeneficiaryFormRow,
  FormValues,
  IBankersBlanketBondDetails,
  IExpatDetails,
  IInsuredDetails,
  IMarineDetails,
  IMarineHullDetails,
  InsuredFormRow,
  IPropertyRiskDetails,
  IRiskDescriptionDetails,
  TravelDetailsFormValues
} from '../types';
import {
  hasPlanCoverEffectiveTo,
  resolveCoverEffectiveFrom,
  resolveCoverEffectiveTo
} from '../helpers/calculateNewPolicyCoverProRataRatio';
import { mapAmendmentPolicyPersonsToMedicalInsureds } from '../helpers/mapAmendmentPolicyPersonsToMedicalInsureds';
import { IMotorDetails } from '../types/getAmendmentMotorDetails';

const buildAddressText = (p?: {
  building?: string | null;
  street?: string | null;
  city?: string | null;
  zone?: { title?: string | null } | null;
  country?: { title?: string | null } | null;
}) => {
  const building = p?.building ?? '';
  const street = p?.street ?? '';
  const zone = p?.zone?.title ?? '';
  const city = p?.city ?? '';
  const country = p?.country?.title ?? '';

  return [building, street, zone, city, country].filter(Boolean).join(', ');
};

export const useAmendmentInitials = (
  data: AmendmentDetailsResponse & AmendmentMoreDetailsResponse,
  planCovers: any[],
  amendmentEffectiveDate: string,
  policyEffectiveDate: string,
  policyExpiryDate: string,
  currencySymbol: string,
  policyPersons: AmendmentPolicyPerson[],
  planCoversLoading = false
) => {
  const details = data?.production?.getAmendmentDetails;
  const ad = details?.amendmentAmendmentDetails;
  const status = details?.status;
  const isCertificateAmendment =
    ad?.amendmentSubType?.code === 'EC11';
  const payerPP = policyPersons?.find((p) => p?.type === 'Payer');
  const ownerPP = policyPersons?.find((p) => p?.type === 'Owner');
  const insuredPPs = policyPersons?.filter((p) => p?.type === 'Insured');
  const beneficiaryPPs = policyPersons?.filter(
    (p) => p?.type === 'Beneficiary'
  );
  const ownerIsDifferent =
    !!payerPP?.personId &&
    !!ownerPP?.personId &&
    payerPP.personId !== ownerPP.personId;

  const mapDtoToCoverForm = (
    dto: AmendmentPolicyCoverDto
  ): AmendmentPolicyCoverForm => {
    const planCover = planCovers?.find(
      (p) => p.cover?.id === dto.policyCoverId
    );
    const planCoverId = planCover?.id ?? dto.policyCoverId;

    const planCoverLabel = planCover
      ? `${planCover.cover?.code ?? ''}-${planCover.cover?.name ?? ''}`
      : dto.policyCover;

    const premiumType =
      (dto.premiumType || '').toUpperCase() === 'AMOUNT'
        ? 'AMOUNT'
        : (dto.premiumType || '').toUpperCase() === 'PERCENTAGE'
          ? 'PERCENTAGE'
          : '';

    const excessTypeRaw = (dto.excessType || '').toUpperCase();
    const allowed = ['AMOUNT', 'PERCENTAGE', 'DAYS'] as const;
    const excessType = allowed.includes(excessTypeRaw as any)
      ? (excessTypeRaw as any)
      : '';

    const isNew = !!dto.isNew;
    const isRemoved = !!dto.isRemoved;
    const coverEffectiveTo = resolveCoverEffectiveTo({
      isRemoved,
      isNew,
      policyPlanCoverEffectiveTo: dto.coverEffectiveTo,
      amendmentEffectiveDate,
      policyExpiryDate
    });
    const coverEffectiveFrom = resolveCoverEffectiveFrom({
      isNew,
      policyPlanCoverEffectiveFrom: dto.coverEffectiveFrom,
      amendmentEffectiveDate
    });
    const planCoverHasEffectiveTo = hasPlanCoverEffectiveTo(dto.coverEffectiveTo);

    const baseSumInsured = FormattingService.parseCurrency(dto.sumInsured);
    const basePremiumAmount =
      premiumType === 'AMOUNT'
        ? FormattingService.parseCurrency(dto.premiumAmount)
        : undefined;

    const basePremiumVariance = FormattingService.parseCurrency(
      dto.premiumVariance
    );
    const basePremiumPercentage =
      premiumType === 'PERCENTAGE'
        ? PrecisionService.multiplyBy100(dto.premiumPercentage as any)
        : undefined;

    const excessAmount =
      excessType === 'AMOUNT'
        ? FormattingService.parseCurrency(dto.excess)
        : undefined;
    const excessPercentage =
      excessType === 'PERCENTAGE'
        ? PrecisionService.multiplyBy100(dto.excess as any)
        : undefined;
    const excessDays =
      excessType === 'DAYS' && dto.excess != null
        ? Number(dto.excess)
        : undefined;

    const newSumInsuredValue =
      dto.newSumInsured != null
        ? FormattingService.parseCurrency(dto.newSumInsured)
        : baseSumInsured;

    const newPremiumAmountValue =
      premiumType === 'AMOUNT' && dto.newPremiumAmount != null
        ? FormattingService.parseCurrency(dto.newPremiumAmount)
        : basePremiumAmount;

    const newPremiumPercentageValue =
      premiumType === 'PERCENTAGE' && dto.newPremiumPercentage != null
        ? PrecisionService.multiplyBy100(dto.newPremiumPercentage as any)
        : basePremiumPercentage;

    const newExcessAmountValue =
      excessType === 'AMOUNT' && dto.newExcess != null
        ? FormattingService.parseCurrency(dto.newExcess)
        : excessAmount;

    const newExcessPercentageValue =
      excessType === 'PERCENTAGE' && dto.newExcess != null
        ? PrecisionService.multiplyBy100(dto.newExcess as any)
        : excessPercentage;

    const newExcessDaysValue =
      excessType === 'DAYS' && dto.newExcess != null
        ? Number(dto.newExcess)
        : excessDays;

    return {
      id: dto.id,
      policyPlanCoverId: planCoverId,
      policyCover: planCoverId ?? '',
      policyCoverName: planCoverLabel,
      isMain: !!dto.isMain,
      isMandatory: !!dto.isMandatory,
      isEditable: dto.isEditable ?? true,
      isNew,
      isRemoved,
      planCoverHasEffectiveTo,
      coverEffectiveFrom,
      coverEffectiveTo,
      sumInsuredIsAdditive: !!dto.additiveSumInsured,
      coverSumInsured: baseSumInsured,
      coverPremiumType: premiumType,
      coverPremiumPercentage: basePremiumPercentage,
      coverPremiumAmount: basePremiumAmount,
      coverPremiumVariance: basePremiumVariance,
      excessOnClaimType: excessType,
      excessOnClaimPercentage: excessPercentage,
      excessOnClaimAmount: excessAmount,
      excessOnClaimDays: excessDays,
      proRataRatio: undefined,
      newSumInsured: newSumInsuredValue,
      newPremiumPercentage: newPremiumPercentageValue,
      newPremiumAmount: newPremiumAmountValue,
      newExcessDays: newExcessDaysValue,
      newExcessAmount: newExcessAmountValue,
      newExcessPercentage: newExcessPercentageValue
    };
  };

  const initialCovers = useMemo(() => {
    // Wait for planCovers to load before processing covers to ensure correct IDs
    if (planCoversLoading) {
      return [];
    }
    const list = details?.policyCovers ?? [];

    const mapped = list.map((dto: AmendmentPolicyCoverDto) =>
      mapDtoToCoverForm(dto)
    );

    return mapped.sort((a, b) => {
      if (a.isNew && !b.isNew) return 1;
      if (!a.isNew && b.isNew) return -1;

      if (a.isNew && b.isNew) {
        const nameA = (a.policyCoverName || '').toLowerCase();
        const nameB = (b.policyCoverName || '').toLowerCase();

        return nameA.localeCompare(nameB);
      }

      return 0;
    });
  }, [
    details?.policyCovers,
    planCovers,
    planCoversLoading,
    amendmentEffectiveDate,
    policyEffectiveDate,
    policyExpiryDate
  ]);

  const initialInsureds: InsuredFormRow[] = useMemo(() => {
    if (!insuredPPs?.length) {
      return [
        {
          id: '',
          code: '',
          fullName: '',
          order: '1',
          addressId: '',
          addressDetails: '',
          limitOfCover: 0
        }
      ];
    }

    return insuredPPs
      .sort(
        (a, b) => (Number(a?.order ?? 0) || 0) - (Number(b?.order ?? 0) || 0)
      )
      .map((pp: any, idx: number) => ({
        id: pp?.personId ?? '',
        code: pp?.code ?? '',
        fullName: pp?.fullName ?? '',
        order: String(pp?.order ?? idx + 1),
        addressId: pp?.addressId ?? '',
        addressDetails: buildAddressText(pp),
        limitOfCover: pp?.limitCover ?? 0
      }));
  }, [insuredPPs]);

  const initialBeneficiaries: BeneficiaryFormRow[] = useMemo(() => {
    if (!beneficiaryPPs?.length) {
      return [];
    }

    return beneficiaryPPs
      .sort(
        (a, b) => (Number(a?.order ?? 0) || 0) - (Number(b?.order ?? 0) || 0)
      )
      .map((pp: any, idx: number) => ({
        id: pp?.personId ?? '',
        code: pp?.code ?? '',
        fullName: pp?.fullName ?? '',
        name: `${pp?.code ?? ''}${
          pp?.code ? ' - ' : ''
        }${pp?.fullName ?? ''}`.trim(),
        addressId: pp?.addressId ?? '',
        addressDetails: buildAddressText(pp),
        beneficiaryPercentage:
          typeof pp?.beneficiaryPercentage === 'number'
            ? PrecisionService.multiplyBy100(pp.beneficiaryPercentage)
            : Number(
                PrecisionService.multiplyBy100(pp.beneficiaryPercentage) ?? 0
              )
      }));
  }, [beneficiaryPPs]);

  const initialInsuredDetails: IInsuredDetails = useMemo(() => {
    return {
      gender: '',
      dateOfBirth: '',
      nationalityID: '',
      occupationClassID: '',
      profession: '',
      passportNumber: ''
    };
  }, []);

  const initialMarineDetails: IMarineDetails = useMemo(() => {
    const marine = data?.production?.getAmendmentPolicyMarine?.[0];

    return {
      matterID: marine?.matterID,
      matter: marine?.matter,
      description: marine?.description,
      vessalName: marine?.vessalName,
      vessalAge: marine?.vessalAge,
      departureFrom: marine?.departureFrom,
      departureTo: marine?.departureTo,
      transportationVia: marine?.transportationVia,
      survey: marine?.survey,
      cargoContract: marine?.cargoContract,
      bank: marine?.bank,
      sumPerShipmentCurrency:
        marine?.sumPerShipmentCurrency ??
        data?.production?.getAmendmentDetails?.policyDetails?.currency,
      sumPerShipmentCurrencyID:
        marine?.sumPerShipmentCurrencyID ??
        data?.production?.getAmendmentDetails?.policyDetails?.currency.id,
      sumPerShipmentCurrencySymbol:
        data?.production?.getAmendmentDetails?.policyDetails?.currency.symbol ||
        '',
      sumPerShipment: marine?.sumPerShipment,
      sumPerShipmentRate: marine?.sumPerShipmentRate ?? 1,
      sumPerShipmentPolicyCurrency:
        (marine?.sumPerShipment ?? 0) * (marine?.sumPerShipmentRate ?? 0),
      lcNumber: marine?.lcNumber,
      lcIssueDate: marine?.lcIssueDate,
      claimsPayableBy: marine?.claimsPayableBy,
      loading: marine?.loading
        ? PrecisionService.multiplyBy100(marine?.loading)
        : 0,
      remarks: marine?.remarks
    };
  }, [data]);

  const initialExpatDetails: IExpatDetails = useMemo(() => {
    const expat = data?.production?.getAmendmentPolicyExpatDetails?.[0];

    return {
      medicalClassID: expat?.medicalClassID ?? '',
      emergency: expat?.emergency ?? '',
      continuity: isEmpty(expat?.continuity)
        ? null
        : expat?.continuity
          ? 'YES'
          : 'NO',
      continuityNumber: expat?.continuityNumber ?? '',
      exclusion: expat?.exclusion ?? '',
      remarks: expat?.remarks ?? ''
    };
  }, [data]);

  const initialMarineHullDetails: IMarineHullDetails = useMemo(() => {
    const marineHull = data?.production?.getAmendmentPolicyMarineHull?.[0];

    return {
      vesselName: marineHull?.vesselName,
      connectionPort: marineHull?.connectionPort,
      territorialWaters: marineHull?.territorialWaters,
      usage: marineHull?.usage,
      builder: marineHull?.builder,
      constructionYear: marineHull?.constructionYear,
      registryPort: marineHull?.registryPort,
      length: marineHull?.length,
      weight: marineHull?.weight,

      height: marineHull?.height,
      engineType: marineHull?.engineType,
      engineNumber: marineHull?.engineNumber,
      engineYearBuilt: marineHull?.engineYearBuilt,
      engineHorsePower: marineHull?.engineHorsePower,
      engineMaxDesignedSpeed: marineHull?.engineMaxDesignedSpeed,
      engineFuel: marineHull?.engineFuel,
      remarks: marineHull?.remarks
    };
  }, [data]);

  const initialBankersDetails: IBankersBlanketBondDetails[] = useMemo(() => {
    return data?.production?.getAmendmentPolicyBankersBlanketBondsDetails.length
      ? data?.production?.getAmendmentPolicyBankersBlanketBondsDetails
      : [
          {
            branchName: '',
            isMain: false,
            isAgency: false,
            employeeNumber: undefined,
            underwritingYear: undefined,
            infedility: undefined,
            burglary: undefined,
            holdup: undefined,
            inSafe: undefined,
            inTransit: undefined,
            forgedCheques: undefined,
            counterfeitCurrencies: undefined,
            clause6: undefined,
            falsification: undefined
          } as IBankersBlanketBondDetails
        ];
  }, [data]);

  const initialMotorDetails: IMotorDetails = useMemo(() => {
    const motor = data?.production?.getAmendmentPolicyMotor?.[0];

    return {
      nameOnLicenseEN: motor?.nameOnLicenseEN,
      nameOnLicenseAR: motor?.nameOnLicenseAR,
      vignetteCode: motor?.vignetteCode,
      vignette: motor?.vignette,
      plateCode: motor?.vehicle?.plateCode?.id,
      plateNumber: motor?.vehicle?.plateNumber,
      usage: motor?.vehicle?.usageType?.id,
      body: motor?.vehicle?.bodyType?.id,
      engineType: motor?.vehicle?.engineType?.id,
      make: motor?.vehicle?.make?.id,
      model: motor?.vehicle?.model?.id,
      color: motor?.vehicle?.color?.id,
      yearOfMake: motor?.vehicle?.yearOfMake,
      engine: motor?.vehicle?.engine,
      weight: motor?.vehicle?.weight,
      engineSize: motor?.vehicle?.engineSize,
      seatingCapacity: motor?.vehicle?.seatingCapacity,
      chassis: motor?.vehicle?.chassis,
      horsePower: motor?.vehicle?.horsePower,
      carValue: motor?.vehicle?.carValue,
      agencyRepairYears: motor?.agencyRepairYears,
      depreciationYears: motor?.depreciationYears
    };
  }, [data]);

  const initialMedicalDetails = useMemo(() => {
    const medical = data?.production?.getAmendmentPolicyMedical?.[0];

    return {
      medicalClassId: medical?.medicalClassId ?? '',
      levelId: medical?.levelId ?? '',
      nssf: medical?.nssf ?? ''
    };
  }, [data]);

  const initialMedicalInsureds = useMemo(() => {
    const rawPolicyPersons =
      (data?.production as any)?.getAmendmentPolicyPersons ?? [];

    return mapAmendmentPolicyPersonsToMedicalInsureds(rawPolicyPersons);
  }, [data]);

  const initialRiskDescriptionDetails: IRiskDescriptionDetails[] =
    useMemo(() => {
      const descriptionOfRisk =
        data?.production?.getAmendmentPolicyRiskDescription ?? [];

      const mapped = descriptionOfRisk.map((item) => ({
        riskDescription: item.riskDescription ?? '',
        geoLocationID: item.geoLocationID ?? '',
        order: item.order ?? 0
      }));

      const sorted = mapped.sort((a, b) => (a.order || 0) - (b.order || 0));

      const withSequentialOrder = sorted.map((item, idx) => ({
        ...item,
        order: idx + 1
      }));

      return withSequentialOrder.length
        ? withSequentialOrder
        : [{ riskDescription: '', geoLocationID: '', order: 1 }];
    }, [data]);

  const initialPropertyRiskDetails: IPropertyRiskDetails[] = useMemo(() => {
    const propertyArray = data?.production?.getAmendmentPolicyProperty;

    if (propertyArray?.length) {
      const mapped = propertyArray.map((property) => ({
        riskDescription: property?.riskDescription ?? '',
        geoLocationID: property?.geoLocationID ?? '',
        location: property?.location ?? '',
        order: property?.order ?? 0,
        planCoverPropertyDetails: property?.planCoverPropertyDetails ?? []
      }));

      const sorted = mapped.sort((a, b) => (a.order || 0) - (b.order || 0));

      const withSequentialOrder = sorted.map((item, idx) => ({
        ...item,
        order: idx + 1
      }));

      return withSequentialOrder;
    }

    return [
      {
        riskDescription: '',
        geoLocationID: '',
        location: '',
        order: 1,
        planCoverPropertyDetails: [
          {
            planCoverID: '',
            premium: '',
            riskRate: 0,
            sumInsured: ''
          }
        ]
      }
    ];
  }, [data]);

  const initialTravelDetails: TravelDetailsFormValues = useMemo(() => {
    const travel = data?.production?.getAmendmentPolicyTravel?.[0];

    return {
      id: travel?.id ?? '',
      departure: travel?.departure?.title ?? '',
      departureId: travel?.departureId ?? '',
      destinationId: travel?.destinationId ?? '',
      duration: travel?.duration ?? 0,
      numberOfAdherent: travel?.numberOfAdherent ?? undefined
    };
  }, [data]);

  const initialValues = useMemo<FormValues>(() => {
    return {
      amendmentDetails: {
        amendmentType: ad?.amendmentType ?? '',
        amendmentSubType: ad?.amendmentSubType?.id ?? '',
        amendmentIssueDate: ad?.amendmentIssueDate ?? '',
        amendmentEffectiveDate,
        amendmentReason: ad.amendmentReason
      },
      chargesAndCost: {
        amendmentCost: details?.costAndChargesDetails?.amendmentCost,
        chargesPercentage:
          details?.costAndChargesDetails?.currentChargesPercentage,
        tpaFees: details?.costAndChargesDetails?.currentTPAFees,
        newChargesPercentage: details?.costAndChargesDetails?.chargesPercentage,
        newTPAFees: details?.costAndChargesDetails?.tpaFees,

        // Cancellation specific fields
        basicPremium: Number(details?.offerDetails?.basicPremium),
        basicPremiumVariance: Number(
          details?.policyQuoteVariance?.basicPremium
        ),
        chargesAmount: Number(details?.offerDetails?.charges),
        chargesAmountVariance: Number(details?.policyQuoteVariance?.charges),
        tpaFeesVariance: Number(details?.policyQuoteVariance?.tpaFees),
        cancellationCost: Number(details?.policyQuoteVariance?.policyCost)
      },

      covers: initialCovers,
      status: status,
      payerOwner: {
        ownerIsDifferent: ownerIsDifferent,
        payer: {
          id: payerPP?.personId ?? '',
          fullName: payerPP?.fullName ?? '',
          addressId: payerPP?.addressId ?? '',
          addressDetails: buildAddressText(payerPP)
        },
        owner: {
          id: ownerPP?.personId ?? '',
          fullName: ownerPP?.fullName ?? '',
          addressId: ownerPP?.addressId ?? '',
          addressDetails: buildAddressText(ownerPP)
        }
      },
      insureds: initialInsureds,
      beneficiaries: initialBeneficiaries,
      marineDetails: initialMarineDetails,
      marineHullDetails: initialMarineHullDetails,
      bankerDetails: initialBankersDetails,
      riskDescriptionDetails: initialRiskDescriptionDetails,
      propertyRiskDetails: initialPropertyRiskDetails,
      expatDetails: initialExpatDetails,
      insuredDetails: initialInsuredDetails,
      motorDetails: initialMotorDetails,
      medicalDetails: initialMedicalDetails,
      documents: details?.documents ?? undefined,
      medicalInsureds: initialMedicalInsureds,
      travelDetails: initialTravelDetails,
      insuredPolicyMedicals: []
    };
  }, [
    ad,
    amendmentEffectiveDate,
    details?.costAndChargesDetails,
    initialCovers,
    initialInsureds,
    initialBeneficiaries,
    details?.policyDetails?.currency?.id,
    currencySymbol,
    initialBankersDetails,
    initialMarineDetails,
    initialMarineHullDetails,
    status,
    policyPersons,
    initialExpatDetails,
    initialInsuredDetails,
    initialMotorDetails,
    initialMedicalInsureds,
    initialMedicalDetails,
    initialTravelDetails
  ]);

  const renderCountRef = useRef(0);
  useEffect(() => {
    renderCountRef.current++;
  }, [initialValues]);

  return { initialValues };
};
